import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import {
  Box,
  HStack,
  IconButton,
  Stack,
  StackProps,
  Text,
} from "@chakra-ui/react";
import { useState } from "react";

interface Props extends StackProps {
  totalItems: number;
  pageSize: number;
  onPageChange?: (page: number) => void;
}

const Pagination = ({ totalItems, pageSize, onPageChange, ...rest }: Props) => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const totalPages = Math.ceil(totalItems / pageSize);

  const generatePageNumbers = () => {
    const pages: (number | string)[] = [];

    // if totalPages <= 5, show all pages
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // if totalPages > 5, add ellipsis
      if (currentPage <= 3) {
        pages.push(1, 2, 3, "...", totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, "...", totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(
          1,
          "...",
          currentPage - 1,
          currentPage,
          currentPage + 1,
          "...",
          totalPages
        );
      }
    }

    return pages;
  };

  const prevPage = () => {
    setCurrentPage((currentPage) => currentPage - 1);
    onPageChange && onPageChange(currentPage - 1);
  };

  const nextPage = () => {
    setCurrentPage((currentPage) => currentPage + 1);
    onPageChange && onPageChange(currentPage + 1);
  };

  const selectPage = (page: number) => {
    setCurrentPage(page);
    onPageChange && onPageChange(page);
  };

  return (
    <Stack
      className="root-pagination"
      w={"full"}
      gap={"8"}
      justify={"center"}
      {...rest}
      display={totalPages > 1 ? "flex" : "none"}
    >
      <HStack
        spacing={{ base: "8px", lg: "12px" }}
        justifyContent={{ base: "center", lg: "end" }}
        w={"full"}
      >
        <IconButton
          icon={<ChevronLeftIcon boxSize={"30px"} />}
          aria-label="Previous Page"
          onClick={prevPage}
          isDisabled={currentPage === 1}
          variant={"gost"}
        />

        {generatePageNumbers().map((page, index) =>
          typeof page === "number" ? (
            <Box
              as={"button"}
              key={index}
              onClick={() => selectPage(page)}
              padding={{ base: "3px 10px", lg: "4px 12px" }}
              rounded={"2px"}
              border={
                page === currentPage ? "solid 1px #F56565" : "solid 1px #CBD5E0"
              }
              color={page === currentPage ? "secondary" : "gray50"}
              fontSize={"12px"}
            >
              {page}
            </Box>
          ) : (
            <Text cursor={"not-allowed"} key={index}>
              {page}
            </Text>
          )
        )}

        <IconButton
          icon={<ChevronRightIcon boxSize={"30px"} />}
          variant={"gost"}
          aria-label="Next Page"
          onClick={nextPage}
          isDisabled={currentPage === totalPages}
        />
      </HStack>
    </Stack>
  );
};

export default Pagination;
